// Pipeline, health and conversion-delivery monitors — the polled reads.
//
// All `realtime`, which means `POLICY.realtime` supplies staleTime 0, a 5-minute
// gcTime, no persistence, and — the part that used to be copy-pasted — the
// 5s → 10s → 20s → 30s retry backoff. Sixteen hooks wrote
// `retryDelay: (failureCount) => withRealtimeBackoff(failureCount)` by hand;
// none of them do now.
//
// Poll intervals come from `CADENCE`, so the three separately-named constants
// that all meant 5000 (`REALTIME_BASE_MS`, `MANAGE_SYNC_POLL_MS`,
// `FLOW_FEED_POLL_MS`) collapse into one tier, and the reason a given read is
// slower than another is stated where the read is declared.

import { api } from '@/api/client';
import { CADENCE, defineQuery } from '@/api/resource';
import type {
  IngestCorrection,
  IngestRun,
  PixelActivityResponse,
  ProductionStat,
  ProductionStatDay,
  ConversionsHealthResponse,
  HealthSummary,
  PipelineMonitorPolicy,
  EventsPaceResponse,
} from '@/api/types';
// Generated bindings; `types.ts` re-exports the hand-written half only.
import type {
  SystemHealthResponse,
} from '@/api/types.gen';

/**
 * Monitor thresholds. It is read beside the polled pipeline resources but is
 * configuration, so the configuration revision—not a timer—controls freshness.
 */
export const pipelinePolicy = defineQuery({
  namespace: 'realtime',
  name: 'pipeline.policy',
  domains: ['configuration'],
  keyOf: () => [],
  request: (_: void, signal) => api.get<PipelineMonitorPolicy>('/pipeline/policy', { signal }),
});

/**
 * Unified ingest-run log — scheduled refresh/backfill plus manual runs.
 *
 * The cadence is overridable at the call site because the chrome health chip
 * watches this ambiently while the pipeline page watches it closely; TanStack
 * uses the smallest active interval when both are mounted.
 */
export const pipelineRuns = defineQuery({
  namespace: 'realtime',
  name: 'pipeline.runs',
  volatile: true,
  keyOf: () => [],
  cadence: CADENCE.fast,
  request: (_: void, signal) => api.get<IngestRun[]>('/pipeline/runs', { signal }),
});

export const pipelineStats = defineQuery({
  namespace: 'realtime',
  name: 'pipeline.stats',
  volatile: true,
  keyOf: () => [],
  cadence: CADENCE.fast,
  request: (_: void, signal) => api.get<ProductionStat[]>('/pipeline/stats', { signal }),
});

export const pipelineStatDetail = defineQuery({
  namespace: 'realtime',
  name: 'pipeline.statDetail',
  volatile: true,
  keyOf: (productionId: string) => [productionId],
  cadence: CADENCE.fast,
  request: (productionId: string, signal) =>
    api.get<ProductionStatDay[]>(
      `/pipeline/stats/${encodeURIComponent(productionId)}`,
      { signal },
    ),
});

export const pipelineCorrections = defineQuery({
  namespace: 'realtime',
  name: 'pipeline.corrections',
  volatile: true,
  keyOf: (productionId: string) => [productionId],
  cadence: CADENCE.fast,
  request: (productionId: string, signal) =>
    api.get<IngestCorrection[]>(
      `/pipeline/corrections/${encodeURIComponent(productionId)}`,
      { signal },
    ),
});

/**
 * Per-(pixel, event_type) web-event counts from `web_events` via the batch tunnel.
 * The 60s cadence matches the server-side KV TTL — activity is a freshness
 * signal, not a 5s dashboard, and polling harder only re-reads the same cache.
 */
export const pixelActivity = defineQuery({
  namespace: 'realtime',
  name: 'pixels.activity',
  volatile: true,
  keyOf: () => [],
  cadence: CADENCE.ambient,
  request: (_: void, signal) => api.get<PixelActivityResponse>('/pixels/activity', { signal }),
  unwrap: (r) => r.rows,
});

/**
 * The single authoritative health read — D1 heartbeat plus tunnel-backed error
 * rates, composed server-side into one HealthStatus per component so the navbar
 * chip, the Health tiles and the alert engine all mean the same "green".
 */
export const healthSummary = defineQuery({
  namespace: 'realtime',
  name: 'health.summary',
  volatile: true,
  keyOf: () => [],
  cadence: CADENCE.ambient,
  request: (_: void, signal) => api.get<HealthSummary>('/health/summary', { signal }),
});

/** System-metric bands: the unified quality thresholds behind every de-hardcoded cell. */
export const healthMetrics = defineQuery({
  namespace: 'realtime',
  name: 'health.metrics',
  volatile: true,
  keyOf: () => [],
  cadence: CADENCE.ambient,
  request: (_: void, signal) => api.get<SystemHealthResponse>('/health/metrics', { signal }),
});

/**
 * Conversion delivery (CAPI forwarding) health. 30s rather than 5s because a
 * delivery cycle is minutes-scale — polling faster shows the same numbers.
 */
export const conversionsHealth = defineQuery({
  namespace: 'realtime',
  name: 'conversions.health',
  volatile: true,
  keyOf: () => [],
  cadence: CADENCE.slow,
  request: (_: void, signal) =>
    api.get<ConversionsHealthResponse>('/insights/conversions-health', { signal }),
});

/**
 * Production-wide net ticket sales, last hour vs the previous one. Ambient like
 * the health reads and tied to the same server-side KV TTL, so a faster poll
 * would re-read an unchanged cache entry.
 */
export const eventsPace = defineQuery({
  namespace: 'realtime',
  name: 'events.pace',
  volatile: true,
  keyOf: () => [],
  cadence: CADENCE.ambient,
  request: (_: void, signal) => api.get<EventsPaceResponse>('/insights/events-pace', { signal }),
});
